// Phone numbers as people type them, and as core stores them: E.164, the form
// SMS sends to (+15551234567). A number typed without a + is read as a US one:
// ten digits, after an optional country code 1. One typed with a + keeps its
// own country code.

const digitsOf = (raw: string) => raw.replace(/\D/g, '');

const startsInternational = (raw: string) => raw.trimStart().startsWith('+');

/** What a phone field says when its number can't be saved. */
export const NOT_A_PHONE =
  'That isn’t a phone number we can save. Enter 10 digits for a US number, or + and the country code.';

/** (555) 123-4567, built up as the digits arrive. */
function formatUS(digits: string): string {
  if (digits.length > 10) return digits; // too long to be a US number: shown as typed, refused on save
  if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return digits;
}

/** The number as it reads while it's typed or pasted. */
export function formatPhone(raw: string): string {
  const digits = digitsOf(raw);
  if (startsInternational(raw)) {
    return digits.startsWith('1') ? `+1 ${formatUS(digits.slice(1))}`.trimEnd() : `+${digits}`;
  }
  return digits.startsWith('1') ? `1 ${formatUS(digits.slice(1))}`.trimEnd() : formatUS(digits);
}

/** A stored number as the field shows it: a US one without its +1. */
export function displayPhone(stored: string): string {
  return /^\+1\d{10}$/.test(stored) ? formatUS(stored.slice(2)) : formatPhone(stored);
}

/**
 * The number in E.164, or null when it can't be a phone number. The rule for a
 * + number is core's (`sms.NormalizePhone`): a country code that isn't 0, and 7
 * to 15 digits in all. A US number also needs an area code that isn't 0 or 1.
 */
export function toE164(raw: string): string | null {
  const digits = digitsOf(raw);
  if (startsInternational(raw) && !digits.startsWith('1')) {
    return /^[1-9]\d{6,14}$/.test(digits) ? `+${digits}` : null;
  }
  const us = digits.startsWith('1') ? digits.slice(1) : digits;
  return /^[2-9]\d{9}$/.test(us) ? `+1${us}` : null;
}
